import React, { useRef, useState } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Image,
  Row,
  Col,
  Typography,
  Divider,
  Space,
  Button,
  Modal,
  message
} from 'antd';
import {
  CarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  CalendarOutlined,
  IdcardOutlined,
  DownloadOutlined,
  AlertOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import useLotesImpresionAPI from '../../lotes-impresion/useLotesImpresionAPI';

const { Title, Text } = Typography;
const cardStyles = {
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.08)',
  borderRadius: '8px',
  transition: 'box-shadow 0.3s ease',
  marginBottom: '16px'
};

const InfraccionDetalle = ({ data }: any) => {
  // Parsear datos JSON anidados
  const linkPago = data.linkPago ? JSON.parse(data.linkPago) : null;
  const telefonosFijo = data.titular?.telefonosFijo ? JSON.parse(data.titular.telefonosFijo) : [];
  const telefonosCelulares = data.titular?.telefonosCelulares ? JSON.parse(data.titular.telefonosCelulares) : [];
  const emails = data.titular?.emails ? JSON.parse(data.titular.emails) : [];
  const [modalVisible, setModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [infraccionPdfUrl, setInfraccionPdfUrl] = useState<string>();
  const apiLoteImpresion = useLotesImpresionAPI();
  const [descargandoPDF, setDescargandoPDF] = useState(false);

  // Filtrar teléfonos que tengan datos
  const telefonosConDatos = [
    ...telefonosFijo.filter((tel: { Area: any; Telefono: any; }) => tel.Area && tel.Telefono),
    ...telefonosCelulares.filter((cel: { CelularArea: any; CelularTelefono: any; }) => cel.CelularArea && cel.CelularTelefono)
  ].slice(0, 3);

  // Formatear fechas
  const formatDate = (dateString: string | number | Date) => {

    if (!dateString) {
      return 'Fecha no disponible';
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return 'Fecha inválida';
    }

    const year = date.getFullYear();
    if (year <= 1) {
      return 'Fecha no disponible';
    }

    if (year < 1900) {
      return 'Fecha no disponible';
    }
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const openImageModal = (url: string) => {
    setCurrentImage(url);
    setModalVisible(true);
    console.log(url)
  };

  const downloadLastPrint = async (id: number) => {
    setDescargandoPDF(true);

    try {
      const response = await apiLoteImpresion.getActaPdf(id);
      setDescargandoPDF(false);
      if (response.error) throw Error;
      setInfraccionPdfUrl(response.data?.value);
    }
    catch (e) {
      message.error("Error al descargar el PDF");
      setDescargandoPDF(false);
    }
  };

  // Calcular montos
  const montoBase = data.valorUF * data.cotizacionUF;
  const descuento = montoBase * (data.porcentajeDescuentoPagoVoluntario / 100);
  const montoConDescuento = montoBase - descuento;
  const montoTotal = montoConDescuento + data.gastosAdministrativos;

  return (
    <>
      <Card>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Space align="center" style={{ marginBottom: '8px' }}>
            <Title level={2} style={{ margin: 0 }}>
              Detalle de Infracción
            </Title>
            <Tag
              color={data.estadoPago === 'IMPAGA' ? 'red' : 'green'}
              style={{ fontSize: '20px', padding: '4px 8px' }}
            >
              {data.estadoPago}
            </Tag>
             <Tag
              color={(data.estado === 'IMPRESION_GENERADA' || data.estado === 'IMPRESION_EXCLUIDA') ? 'green' : 'orange'}
              style={{ fontSize: '20px', padding: '4px 8px' }}
            >
              {data.estado}
            </Tag>
          </Space>
          <Text type="secondary" style={{ fontSize: '15px' }}>
            Trámite: {data.nroTramite == undefined ? 'IMPRESION PENDIENTE' : data.nroTramite} | ID: {data.id}
          </Text>
        </div>

        <Row gutter={[24, 24]}>
          {/* Columna izquierda - Información principal */}
          <Col xs={24} lg={12}>
            {/* Información de la infracción */}
            <Card
              title={
                <Space>
                  <CarOutlined />
                  Información de la Infracción
                </Space>
              }
              headStyle={{ backgroundColor: '#f2fce9ff' }}
              size="small"
              style={cardStyles}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Patente">
                  <Tag color="blue">{data.patente}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Vehículo">
                  {data.titular?.marcaVehiculo ?? 'SIN DATOS'} {data.titular?.modeloVehiculo ?? 'SIN DATOS'}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo">
                  {data.titular?.tipoVehiculo ?? 'SIN DATOS'}
                </Descriptions.Item>
                <Descriptions.Item label="Lugar">
                  <Space>
                    <EnvironmentOutlined />
                    {data.lugar}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Fecha Infracción">
                  {formatDate(data.fechaInfraccion)}
                </Descriptions.Item>
                <Descriptions.Item label="Artículo">
                  <Text strong>{data.articuloCodigo}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Descripción">
                  {data.descripcionCodigo}
                </Descriptions.Item>
                <Descriptions.Item label="Unidad de Producción">
                  {data.unidadProduccionNombre}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Información de pago */}
            <Card
              title={
                <Space>
                  <DollarOutlined />
                  Información de Pago
                </Space>
              }
              headStyle={{ backgroundColor: '#f2fce9ff' }}
              size="small"
              style={cardStyles}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Estado">
                  <Tag color={data.estadoPago === 'IMPAGA' ? 'red' : 'green'}>
                    {data.estadoPago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Referencia de Pago">
                  <Text code>{data.referenciaPago == undefined ? 'IMP. PENDIENTE' : data.referenciaPago}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Vencimiento Pago Voluntario">
                  <CalendarOutlined />&nbsp;{data.vencimientoPagoVountario == null ? 'IMP. PENDIENTE' : formatDate(data.vencimientoPagoVountario)}
                </Descriptions.Item>
                <Descriptions.Item label="Vencimiento Pago Total">
                  <CalendarOutlined />&nbsp;{data.fecha2doVencimiento == undefined ? 'IMP. PENDIENTE' : formatDate(data.fecha2doVencimiento)}
                </Descriptions.Item>
                <Descriptions.Item label="Descuento Pago Voluntario">
                  <Tag color="green">{data.porcentajeDescuentoPagoVoluntario}%</Tag>
                </Descriptions.Item>
                {linkPago?.url_qr && (
                  <Descriptions.Item label="Pago QR">
                    <Button
                      type="link"
                      href={linkPago.url_qr}
                      target="_blank"
                      icon={<DollarOutlined />}
                    >
                      Abrir QR de Pago
                    </Button>
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Divider />

              {/* Desglose de montos */}
              <Title level={5}>Desglose de Montos</Title>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Row justify="space-between">
                  <Text>Monto Base ({data.valorUF} UF × ${data.cotizacionUF}):</Text>
                  <Text>${montoBase.toLocaleString('es-AR')}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>Descuento ({data.porcentajeDescuentoPagoVoluntario}%):</Text>
                  <Text type="success">-${descuento.toLocaleString('es-AR')}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>Gastos Administrativos:</Text>
                  <Text>${data.gastosAdministrativos.toLocaleString('es-AR')}</Text>
                </Row>
                <Divider style={{ margin: '8px 0' }} />
                <Row justify="space-between">
                  <Text strong>Total a Pagar:</Text>
                  <Text strong>${montoTotal.toLocaleString('es-AR')}</Text>
                </Row>
              </Space>
            </Card>
          </Col>

          {/* Columna derecha - Información del titular e imágenes */}
          <Col xs={24} lg={12}>
            {/* Información del titular */}
            <Card
              title={
                <Space>
                  <UserOutlined />
                  Información del Titular
                </Space>
              }

              headStyle={{ backgroundColor: '#f2fce9ff' }}
              size="small"
              style={cardStyles}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Nombre">
                  <Text strong>{data.titular?.nombreCompletoTitular ?? '-'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="CUIL">
                  {data.titular?.cuilTitular ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Documento">
                  {data.titular?.tipoDocumentoTitular ?? 0 === 0 ? 'DNI' : 'Otro'}: {data.titular?.documentoTitular ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Dirección">
                  <Space direction="vertical" size={0}>
                    <Text>{data.titular?.direccionCompletaTitular ?? '-'}</Text>
                    <Text type="secondary">
                      {data.titular?.localidadTitular ?? '-'}, {data.titular?.provinciaTitular ?? '-'}
                    </Text>
                    <Text type="secondary">CP: {data.titular?.codigoPostalTitular ?? '-'}</Text>
                  </Space>
                </Descriptions.Item>

                {/* Teléfonos */}
                {telefonosConDatos.length > 0 && (
                  <Descriptions.Item label="Teléfonos">
                    <Space direction="vertical" size={2}>
                      {telefonosConDatos.map((telefono, index) => (
                        <Text key={index}>
                          {telefono.Area || telefono.CelularArea} - {telefono.Telefono || telefono.CelularTelefono}
                          {telefono.TieneWhatsapp === 'S' && (
                            <Tag color="green" style={{ marginLeft: '4px' }}>
                              WhatsApp
                            </Tag>
                          )}
                        </Text>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}

                {/* Emails */}
                {emails.filter((email: any) => email.Email).length > 0 && (
                  <Descriptions.Item label="Emails">
                    <Space direction="vertical" size={2}>
                      {emails.slice(0, 3).map((email: any, index: number) => (
                        email.Email && <Text key={index}>{email.Email}</Text>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {data.imagenes && data.imagenes.length > 0 && (
              <Card
                headStyle={{ backgroundColor: '#f2fce9ff' }}
                size="small"
                style={cardStyles}
                title={
                <Space>
                  <VideoCameraOutlined />
                  Evidencia fotográfica
                </Space>
              }
              >
                <Row gutter={[8, 8]}>


                  {data.imagenes.map((imagen: any, index: number) => (
                    <Col xs={24} sm={8} key={imagen.id}>
                      <Card
                        size="small"
                        bodyStyle={{
                          padding: '8px',
                          height: '220px', // Altura fija para todos los cards
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        hoverable
                        onClick={() => openImageModal(imagen.fullUrl)}
                      >
                        <Image
                          src={imagen.fullUrl}
                          preview={{
                            mask: 'Ver imagen',
                            visible: false
                          }} />
                        <div style={{ textAlign: 'center', marginTop: '8px' }}>
                          <Text type="secondary">
                            {imagen.etiqueta ? `Imagen ${imagen.etiqueta}` : `Evidencia ${index + 1}`}
                          </Text>
                        </div>
                      </Card>
                    </Col>
                  ))}

                </Row>

              </Card>
            )}
          </Col>
        </Row>

        {/* Información adicional en el footer */}
        <Divider />
        <Row justify="space-between" align="middle">
          <Col>
            <Text type="secondary">
              Emisión: {data.fechaEmision != undefined ? formatDate(data.fechaEmision) : 'NO EMITIDA'} |
              Estado: <Tag>{data.estado}</Tag>
              {/* | Juzgamiento: <Tag>{data.estadoJuzgamiento}</Tag> */}
            </Text>
          </Col>
          <Col>
            {data.regenerada === 1 && (
              <Tag color="orange">Regenerada</Tag>
            )}
          </Col>
          <Col>
            <Space>
              {data.regenerada === 1 && (
                <Tag color="orange">Regenerada</Tag>
              )}
              {(data.estado === 'IMPRESION_GENERADA' || data.estado === 'IMPRESION_EXCLUIDA') && (
                <Button
                  type="primary"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadLastPrint(data.id)}
                  loading={descargandoPDF}
                >
                  Descargar PDF
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>
      {/* Modal preview imagenes */}
      <Modal
        open={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        centered // ¡Centrado perfecto!
        width="90%"
        style={{ maxWidth: '1000px' }}
        bodyStyle={{
          padding: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
        destroyOnClose
      >
        {currentImage && (
          <img
            src={currentImage}
            alt="Vista previa"
            style={{
              maxHeight: '85vh',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
          />
        )}
      </Modal>

      <Modal
        width={"90%"}
        style={{ maxWidth: "1024px" }}
        bodyStyle={{ height: "80vh", overflow: "auto" }}
        onCancel={() => setInfraccionPdfUrl(undefined)}
        open={infraccionPdfUrl !== undefined}
        destroyOnClose>
        {infraccionPdfUrl && <iframe style={{ width: "100%", height: "100%" }} src={`${infraccionPdfUrl}?timestamp=${new Date().getTime()}`} />}
      </Modal>
    </>
  );
};

export default InfraccionDetalle;